import { Check, CheckCircle2, Loader2, ShieldCheck, Wrench, X } from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";
import type { HealPatch, HealStage, ProposedHeal } from "@/services/testing";
import { describeQueryStep } from "./typedQuery";

/**
 * Heal v2 is a PROPOSAL flow. The healer diagnoses a failed run, patches the plan's IR, verifies the
 * patched plan against a real run, and then asks. It never mutates the source run's verdict and
 * never applies a patch on its own — a failed run stays failed until the user accepts.
 */

const STAGE_ORDER: HealStage[] = [
	"triage_gate",
	"grounding",
	"generating",
	"validating",
	"intent_gate",
	"verifying",
];

const STAGE_LABEL: Record<HealStage, string> = {
	triage_gate: "Checking the failure is fixable",
	grounding: "Re-reading your org",
	generating: "Drafting a patch",
	validating: "Validating the patch",
	intent_gate: "Checking it still tests what you asked",
	verifying: "Verifying with a real run",
	proposed: "Proposal ready",
	declined: "No fix worth proposing",
};

export function healStageLabel(stage: HealStage | null | undefined): string {
	return stage ? STAGE_LABEL[stage] : "Preparing fix proposal…";
}

/** Live progress while the heal stream runs — triage_gate … verifying. */
export function HealStageProgress({
	stage,
	attempt,
	maxAttempts,
	triageCategory,
}: {
	stage?: HealStage | null;
	attempt?: number | null;
	maxAttempts?: number | null;
	triageCategory?: string | null;
}) {
	const currentIndex = stage ? STAGE_ORDER.indexOf(stage) : -1;
	return (
		<div className="relative overflow-hidden rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs">
			<div className="pointer-events-none absolute inset-0 -translate-x-full animate-[shimmer_2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
			<div className="relative flex items-center gap-2">
				<Wrench className="h-3.5 w-3.5 shrink-0 animate-pulse text-primary" />
				<span className="shrink-0 font-semibold text-primary">Preparing fix proposal</span>
				<span className="min-w-0 truncate text-foreground/80">{healStageLabel(stage)}</span>
				{attempt != null && maxAttempts != null && maxAttempts > 1 && (
					<span className="shrink-0 rounded bg-primary/20 px-1.5 py-0.5 font-mono text-[11px] text-primary">
						attempt {attempt}/{maxAttempts}
					</span>
				)}
				{triageCategory && (
					<span className="shrink-0 rounded border border-border/70 bg-card/60 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
						{triageCategory}
					</span>
				)}
			</div>
			<div className="relative mt-1.5 flex flex-wrap items-center gap-1">
				{STAGE_ORDER.map((s, i) => {
					const done = currentIndex > i;
					const active = currentIndex === i;
					return (
						<span
							key={s}
							title={STAGE_LABEL[s]}
							className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
								done
									? "bg-success/15 text-success-strong"
									: active
										? "bg-primary/25 font-semibold text-primary"
										: "bg-muted/40 text-muted-foreground/60"
							}`}
						>
							{s}
						</span>
					);
				})}
			</div>
		</div>
	);
}

/** One-line summary of an opaque IR step — enough to see WHAT the patch swaps in. */
export function describeIrStep(step: Record<string, unknown> | undefined): string {
	if (!step) return "(step removed)";
	const type = typeof step.type === "string" ? step.type : "step";
	const label =
		[step.label, step.say, step.name, step.actionName, step.text].find(
			(v) => typeof v === "string" && v.trim(),
		) ?? "";
	const query = describeQueryStep(step);
	const parts = [type];
	if (label) parts.push(String(label));
	if (query) parts.push(query);
	return parts.join(" · ");
}

function PatchRow({ patch }: { patch: HealPatch }) {
	const isRemove = patch.op === "remove";
	return (
		<li className="flex items-start gap-1.5 rounded border border-border/60 bg-card/60 px-1.5 py-1">
			<span
				className={`shrink-0 rounded px-1 py-0.5 font-mono text-[11px] font-semibold ${
					isRemove
						? "bg-destructive/15 text-destructive-strong"
						: "bg-warning/15 text-warning-strong"
				}`}
			>
				{patch.op}
			</span>
			<span className="shrink-0 font-mono text-[11px] text-muted-foreground">
				step {patch.stepIndex}
			</span>
			<span className="min-w-0 flex-1 break-words font-mono text-[11px] text-foreground/85">
				{isRemove ? "delete this step" : describeIrStep(patch.step)}
			</span>
		</li>
	);
}

export function HealProposalCard({
	proposal,
	deciding,
	onAccept,
	onReject,
}: {
	proposal: ProposedHeal;
	deciding?: boolean;
	onAccept: () => void;
	onReject: () => void;
}) {
	return (
		<div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-2.5 text-xs">
			<div className="flex flex-wrap items-center gap-2">
				<Wrench className="h-3.5 w-3.5 shrink-0 text-warning-strong" />
				<span className="font-semibold text-warning-strong">Proposed fix</span>
				<span className="rounded border border-border/70 bg-card/60 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
					{proposal.triage_category}
				</span>
				<span className="ml-auto font-mono text-[11px] text-muted-foreground">
					failing step {proposal.failing_step_index}
				</span>
			</div>

			<p className="leading-relaxed text-foreground/90">{proposal.diagnosis}</p>

			{proposal.patches.length > 0 && (
				<div>
					<div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
						{proposal.patches.length} change{proposal.patches.length === 1 ? "" : "s"} to the plan
					</div>
					<ul className="space-y-1">
						{proposal.patches.map((patch, i) => (
							<PatchRow key={`${patch.op}-${patch.stepIndex}-${i}`} patch={patch} />
						))}
					</ul>
				</div>
			)}

			{/* The verification run executed the PATCHED plan and passed — that is what makes this
			    proposal trustworthy, so state it explicitly rather than asking for blind faith. */}
			<div className="flex items-center gap-1.5 rounded border border-success/30 bg-success/5 px-2 py-1 text-[11px] text-success-strong">
				<ShieldCheck className="h-3.5 w-3.5 shrink-0" />
				<span>
					Verified green in run{" "}
					<span className="font-mono">{proposal.verification_run_id.slice(0, 8)}</span>
				</span>
			</div>

			<div className="flex flex-wrap items-center gap-1.5 border-t border-border/40 pt-2">
				<Button
					size="sm"
					className="h-7 text-xs"
					onClick={onAccept}
					disabled={deciding}
					title="Apply these changes to the plan"
				>
					{deciding ? (
						<Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
					) : (
						<Check className="mr-1 h-3.5 w-3.5" />
					)}
					Accept fix
				</Button>
				<Button
					size="sm"
					variant="outline"
					className="h-7 text-xs"
					onClick={onReject}
					disabled={deciding}
					title="Discard this proposal — the plan is left untouched"
				>
					<X className="mr-1 h-3.5 w-3.5" />
					Reject
				</Button>
				<span className="ml-auto text-[11px] text-muted-foreground">
					Your plan is unchanged until you accept.
				</span>
			</div>
		</div>
	);
}

/** The healer looked and found nothing worth proposing — a normal outcome, not an error. */
export function HealDeclinedNote({ reason, notes }: { reason?: string | null; notes?: string[] }) {
	return (
		<div className="space-y-1 rounded-md border border-border bg-muted/20 p-2 text-xs text-muted-foreground">
			<div className="flex items-center gap-1.5 font-semibold text-foreground/80">
				<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
				No fix proposed
			</div>
			<p className="leading-relaxed">
				{reason ?? "The healer did not find a change it could safely propose for this failure."}
			</p>
			{notes && notes.length > 0 && (
				<ul className="ml-4 list-disc space-y-0.5 opacity-90">
					{notes.map((n, i) => (
						<li key={i}>{n}</li>
					))}
				</ul>
			)}
		</div>
	);
}
